'use client';

import Link from 'next/link';
import { Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { dancerPath } from '@kit/shared/handles';
import {
  EntityCard,
  EntityCardBookmark,
  EntityCardCaption,
  EntityCardCover,
  EntityCardKicker,
  EntityCardSub,
  EntityCardTitle,
} from '@kit/ui/entity-card';

import { BookmarkButton } from '~/lib/bookmarks/components/bookmark-button';
import type { DancerListItem } from '../_lib/server/get-dancers';

// EN fallbacks; live labels resolve through `dancers:levels.<value>`.
const DANCE_LEVEL_DEFAULT_LABELS: Record<string, string> = {
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  prePro: 'Pre-Pro',
  pro: 'Professional',
};

function countryCodeToFlag(code: string): string {
  return code
    .toUpperCase()
    .split('')
    .map((char) => String.fromCodePoint(0x1f1e6 - 65 + char.charCodeAt(0)))
    .join('');
}

interface DancerCardProps {
  dancer: DancerListItem;
  priority?: boolean;
  /** Super-admin only (BAL-392): renders an Edit affordance in the card's
   * overlay slot linking to the admin artist editor. The flag is resolved
   * per-request at the page level — NEVER derive it inside the cached
   * listing helper, which is shared across all viewers. */
  showAdminEdit?: boolean;
}

function getDisplayName(
  dancer: DancerListItem,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (dancer.stage_name) return dancer.stage_name;
  if (dancer.first_name && dancer.last_name) {
    return `${dancer.first_name} ${dancer.last_name}`;
  }
  if (dancer.first_name) return dancer.first_name;
  return t('community:dancers.fallbackName');
}

function getInitials(dancer: DancerListItem): string {
  if (dancer.stage_name) {
    return dancer.stage_name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0])
      .join('')
      .toUpperCase();
  }
  if (dancer.first_name && dancer.last_name) {
    return `${dancer.first_name[0]}${dancer.last_name[0]}`.toUpperCase();
  }
  if (dancer.first_name) return dancer.first_name.slice(0, 2).toUpperCase();
  return 'DA';
}

export function DancerCard({
  dancer,
  priority = false,
  showAdminEdit = false,
}: DancerCardProps) {
  const { t } = useTranslation();
  const fullName = getDisplayName(dancer, t);
  const initials = getInitials(dancer);

  const profileUrl = dancer.slug
    ? dancerPath(dancer.slug)
    : dancerPath(dancer.id);

  const locationLabel = (() => {
    const flag = dancer.country_code
      ? countryCodeToFlag(dancer.country_code)
      : '';
    if (dancer.city && flag) return `${flag} ${dancer.city}`;
    if (dancer.city) return dancer.city;
    if (flag) return flag;
    return null;
  })();

  // Prefer the derived community level (pro / prePro) so the tile matches the
  // Professional / Pre-Professional chips. Only fall back to the legacy skill
  // field for the levels the derivation does NOT cover (beginner /
  // intermediate) — never legacy 'pro'/'prePro', or an excluded dancer (e.g. an
  // amateur) carrying a stale Meteor `dance_level='pro'` would be mislabelled
  // "Professional", contradicting their exclusion from that chip.
  const legacyFallback =
    dancer.dance_level === 'beginner' || dancer.dance_level === 'intermediate'
      ? dancer.dance_level
      : null;
  const effectiveLevel = dancer.discovery_level ?? legacyFallback;

  const levelLabel = effectiveLevel
    ? DANCE_LEVEL_DEFAULT_LABELS[effectiveLevel]
      ? t(`dancers:levels.${effectiveLevel}`, {
          defaultValue: DANCE_LEVEL_DEFAULT_LABELS[effectiveLevel],
        })
      : effectiveLevel
    : null;

  const stylesLabel = (() => {
    if (!dancer.dance_styles || dancer.dance_styles.length === 0) return null;
    return dancer.dance_styles
      .slice(0, 2)
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(' · ');
  })();

  const kickerLabel = [levelLabel, stylesLabel].filter(Boolean).join('  ·  ');
  const isVerified = !!(dancer.is_verified && dancer.is_premium);

  return (
    <EntityCard
      href={profileUrl}
      aspect="3/4"
      ariaLabel={fullName}
      fallbackSeed={dancer.id}
      testId="dancer-card"
    >
      {/* Single Bookmark slot: EntityCard lifts only the FIRST
          EntityCard.Bookmark child outside its Link, so the edit Link must
          share this wrapper — a second slot would nest an anchor inside the
          card anchor. */}
      <EntityCardBookmark>
        <div className="flex items-center gap-1.5">
          {showAdminEdit && dancer.user_id && (
            <Link
              href={`/admin/artists/${dancer.user_id}`}
              aria-label={t('common:routes.editProfile')}
              title={t('common:routes.editProfile')}
              data-testid="dancer-card-edit-link"
              className="bg-background/80 hover:bg-background flex size-7 items-center justify-center rounded-full backdrop-blur-md"
            >
              <Pencil className="size-3.5" />
            </Link>
          )}
          <BookmarkButton
            bookmarkableType="professional_profile"
            bookmarkableId={dancer.id}
            variant="icon"
            size="sm"
            className="bg-background/80 hover:bg-background size-7 border-0 backdrop-blur-md"
          />
        </div>
      </EntityCardBookmark>

      <EntityCardCover
        src={dancer.profile_photo_url}
        alt={fullName}
        fallbackInitials={initials}
        priority={priority}
      />

      <EntityCardCaption>
        <EntityCardTitle
          verified={isVerified}
          verifiedLabel={t('community:dancers.verifiedDancer')}
        >
          {fullName}
        </EntityCardTitle>

        {locationLabel && <EntityCardSub>{locationLabel}</EntityCardSub>}

        {kickerLabel && <EntityCardKicker>{kickerLabel}</EntityCardKicker>}
      </EntityCardCaption>
    </EntityCard>
  );
}
